import { Badge, Typography } from '@mantine/core';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkDirective from 'remark-directive';

type MarkdownNode = {
  type: string;
  name?: string;
  attributes?: Record<string, string | null>;
  children?: MarkdownNode[];
  data?: {
    hName?: string;
    hProperties?: Record<string, unknown>;
  };
  position?: {
    start: { offset?: number };
    end: { offset?: number };
  };
  value?: string;
};

export type BadgeColor = 'gray' | 'blue' | 'green' | 'yellow' | 'red';

const DEFAULT_BADGE_COLOR: BadgeColor = 'blue';
const BADGE_COLORS = new Set<string>([
  'gray',
  'blue',
  'green',
  'yellow',
  'red',
]);

const isBadgeColor = (color: string | null | undefined): color is BadgeColor =>
  Boolean(color && BADGE_COLORS.has(color));

const remarkBadgeDirective = () => {
  return (tree: MarkdownNode, file: { value: unknown }) => {
    const source = String(file.value);

    const transform = (node: MarkdownNode): MarkdownNode => {
      const isDirective = node.type.endsWith('Directive');

      if (node.type === 'textDirective' && node.name === 'badge') {
        const requestedColor = node.attributes?.color;
        const color = isBadgeColor(requestedColor)
          ? requestedColor
          : DEFAULT_BADGE_COLOR;

        node.data = {
          ...node.data,
          hName: 'span',
          hProperties: {
            dataBadgeDirective: true,
            dataBadgeColor: color,
          },
        };
      } else if (isDirective) {
        const start = node.position?.start.offset;
        const end = node.position?.end.offset;

        if (start !== undefined && end !== undefined) {
          return { type: 'text', value: source.slice(start, end) };
        }
      }

      if (node.children) {
        node.children = node.children.map(transform);
      }

      return node;
    };

    transform(tree);
  };
};

const markdownComponents: Components = {
  span: ({ node, children, ...props }) => {
    if (node?.properties.dataBadgeDirective === true) {
      const color = node.properties.dataBadgeColor;

      return (
        <Badge
          color={typeof color === 'string' ? color : DEFAULT_BADGE_COLOR}
          size="sm"
        >
          {children}
        </Badge>
      );
    }

    return <span {...props}>{children}</span>;
  },
};

export const MarkdownNote = ({ content }: { content: string }) => {
  return (
    <Typography fz="sm">
      <ReactMarkdown
        components={markdownComponents}
        remarkPlugins={[remarkDirective, remarkBadgeDirective]}
      >
        {content}
      </ReactMarkdown>
    </Typography>
  );
};

export const insertEtcNoteBadge = (
  label: string,
  color?: BadgeColor,
): string => {
  const escapedLabel = label.replaceAll('\\', '\\\\').replaceAll(']', '\\]');

  return `:badge[${escapedLabel}]${color ? `color="${color}"` : ''}`;
};

export const parseFirstEtcNoteBadge = (
  text: string,
): { label: string; color: BadgeColor } | undefined => {
  /**
   :badge[標籤文字]{color="顏色"}
   */
  const match = /:badge\[((?:\\.|[^\\\]])*)\](?:\{color="([^"]*)"\})?/.exec(
    text,
  );

  if (!match) return undefined;

  const [, encodedLabel, requestedColor] = match;

  return {
    label: encodedLabel.replace(/\\([\\\]])/g, '$1'),
    color: isBadgeColor(requestedColor) ? requestedColor : DEFAULT_BADGE_COLOR,
  };
};
